/**
 * Devtools helper: regenerate the FME site-data export GeoJSON for a project, in the browser.
 *
 * Iterating on the export schema with the GIS/FME team otherwise means running a real export and
 * reading the staged S3 objects — but a run that exhausts its retries deletes them
 * (`cleanupExtractExports`), leaving roughly a one-minute window. This produces the same bytes
 * without an export, in whichever environment the app is pointed at.
 *
 * Usage — from the devtools console on any project page:
 *   cgSiteData()                      // summary + invariant check
 *   cgSiteData({ download: true })    // also save potholes/fieldNotes .geojson
 *   cgSiteData({ projectId: '...' })  // defaults to the project in the URL
 *
 * Full FeatureCollections are left on `window.cgSiteDataResult` for poking at.
 *
 * Fidelity: the serializers and layer→type map are imported from the same modules the dispatch job
 * uses, so features cannot drift from production. What is reconstructed here is the job's INPUT —
 * see the archived-file and pagination warnings below.
 */
import {
  SITE_DATA_FIELD_NOTES,
  SITE_DATA_GEO_ITEM_TYPES,
  SITE_DATA_POTHOLES,
} from '@civilgrid/utils/src/extract/constants';
// Type-only: erased at build, so the serializers stay behind the dynamic import below.
import type { ExportGeoItemRow } from '@civilgrid/utils/src/geoItems/exportFeatures';
import { getAxiosClient } from '~/api/common';

/** Bound in the job by MAX_GEO_ITEMS_PAGE; a response at the ceiling is likely truncated. */
const SUSPECT_PAGE_SIZES = new Set([100, 250, 500, 1000]);

type AnyRow = Record<string, any>;

function summarize(potholes: AnyRow[], fieldNotes: AnyRow[]) {
  const sources: Record<string, number> = {};
  let mediaRefs = 0;
  let noMeasurements = 0;
  let populatedAccuracy = 0;

  for (const f of potholes) {
    const p = f.properties;
    for (const m of p.utilityMeasurements ?? []) sources[m.measurementSource] = (sources[m.measurementSource] ?? 0) + 1;
    if (!p.utilityMeasurements?.length) noMeasurements += 1;
    if (Object.values(p.accuracy ?? {}).some((v) => v !== null)) populatedAccuracy += 1;
    mediaRefs += (p.media ?? []).length;
  }
  for (const f of fieldNotes) if (f.properties.media) mediaRefs += 1;

  return { sources, mediaRefs, noMeasurements, populatedAccuracy };
}

/**
 * Invariants worth failing on. The media check is the important one: the GeoJSON carries file ids
 * and the resources index presigns them separately, so a reference the index would not serve
 * reaches FME as a dangling id.
 */
function check(potholes: AnyRow[], fieldNotes: AnyRow[], activeFileIds: Set<string>) {
  const problems: string[] = [];
  const reserved = [
    'geoItemId',
    'geoItemType',
    'status',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
    'accuracy',
    'civilgridUrl',
  ];

  for (const f of [...potholes, ...fieldNotes]) {
    const p = f.properties;
    const media = Array.isArray(p.media) ? p.media : p.media ? [p.media] : [];
    for (const m of media) {
      if (!activeFileIds.has(m.fileId)) problems.push(`media ${m.fileId} on ${p.geoItemId} has no active file`);
      if (m.thumbnailId && m.thumbnailId !== `${m.fileId}:thumbnail`) {
        problems.push(`thumbnailId ${m.thumbnailId} does not match its fileId`);
      }
    }
    for (const key of reserved) if (!(key in p)) problems.push(`${p.geoItemId} is missing reserved key ${key}`);
  }
  return problems;
}

function saveFile(name: string, body: unknown) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(body)], { type: 'application/geo+json' }));
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function cgSiteData(opts: { projectId?: string; download?: boolean } = {}) {
  const projectId = opts.projectId ?? window.location.pathname.match(/\/projects\/([^/]+)/)?.[1];
  if (!projectId) throw new Error('No projectId — pass one or run this from a project page.');

  // Loaded on demand so none of this ships in the main bundle.
  const { buildFieldNoteFeature, buildPotholeFeature } = await import('@civilgrid/utils/src/geoItems/exportFeatures');

  // Raw rows on purpose: listGeoItemsV2 maps dates to Luxon objects, which the serializers would
  // pass straight through into the output.
  const { data: rows } = await getAxiosClient().get<AnyRow[]>(`/v1/projects/${projectId}/geoItems`);

  if (SUSPECT_PAGE_SIZES.has(rows.length)) {
    console.warn(`[cgSiteData] ${rows.length} rows returned — exactly a page size, so this may be truncated.`);
  }

  const active = rows.filter((r: AnyRow) => (r.status ?? 'active') === 'active');
  // The job's loader excludes archived files in SQL; mirror that here or media ids will reference
  // files the resources index refuses to presign.
  const archivedFiles = active.reduce(
    (n: number, r: AnyRow) => n + (r.files ?? []).filter((f: AnyRow) => f.status && f.status !== 'active').length,
    0,
  );
  if (archivedFiles)
    console.warn(`[cgSiteData] excluded ${archivedFiles} archived file(s), matching the export loader.`);

  const withActiveFiles = (r: AnyRow): AnyRow => ({
    ...r,
    files: (r.files ?? []).filter((f: AnyRow) => (f.status ?? 'active') === 'active'),
  });

  const ofType = (types: string[]): ExportGeoItemRow[] =>
    active.filter((r: AnyRow) => types.includes(r.type)).map(withActiveFiles) as ExportGeoItemRow[];
  const potholeRows = ofType(SITE_DATA_GEO_ITEM_TYPES[SITE_DATA_POTHOLES]);
  const fieldNoteRows = ofType(SITE_DATA_GEO_ITEM_TYPES[SITE_DATA_FIELD_NOTES]);

  const linkContext = { projectId, origin: window.location.origin };
  const potholes = {
    type: 'FeatureCollection' as const,
    features: potholeRows.map((r) => buildPotholeFeature(r, linkContext)),
  };
  const fieldNotes = {
    type: 'FeatureCollection' as const,
    features: fieldNoteRows.map((r) => buildFieldNoteFeature(r, linkContext)),
  };

  const activeFileIds = new Set<string>(
    [...potholeRows, ...fieldNoteRows].flatMap((r) => (r.files ?? []).map((f: AnyRow) => f.id)),
  );
  const stats = summarize(potholes.features, fieldNotes.features);
  const problems = check(potholes.features, fieldNotes.features, activeFileIds);

  console.table({
    potholes: { features: potholes.features.length, bytes: JSON.stringify(potholes).length },
    fieldNotes: { features: fieldNotes.features.length, bytes: JSON.stringify(fieldNotes).length },
  });
  console.log('[cgSiteData] measurement sources', stats.sources);
  console.log(
    `[cgSiteData] ${stats.mediaRefs} media reference(s) vs ${activeFileIds.size} active file(s)` +
      ` · ${stats.noMeasurements} pothole(s) with no utilities · ${stats.populatedAccuracy} with GPS accuracy`,
  );
  if (problems.length) console.error(`[cgSiteData] ${problems.length} problem(s)`, problems);
  else console.log('[cgSiteData] invariants OK');

  if (opts.download) {
    saveFile('potholes.geojson', potholes);
    saveFile('fieldNotes.geojson', fieldNotes);
  }

  const result = { projectId, potholes, fieldNotes, problems, stats };
  (window as any).cgSiteDataResult = result;
  return result;
}

/** Attaches the helper. Called once at startup; the serializer itself stays lazily imported. */
export function registerSiteDataPreview() {
  (window as any).cgSiteData = cgSiteData;
}
